// Tlačítko „⚑ Nahlásit“ v hlavičce (F10 10.1 úraz, F11 11.4 provoz, F12 12.5 závada aplikace a námět). Zásuvka
// s formulářem (`NahlasitUraz.tsx` — fotky, kniha úrazů, události ISŘ, hlášení k aplikaci) se stahuje až při
// otevření, ať hlavička netáhne celý formulář do hlavního balíku aplikace (rozdělení balíku 24. 9. 2026).
import type { Ja } from '@ecoser/shared';
import { Ikona } from './ikony';
import { useEffect, useRef, useState } from 'react';
import { AKCE_PODAT } from '../api/hlaseni';
import { AKCE_UDALOST_NAHLASIT } from '../api/iso-udalosti';
import { AKCE_URAZ_ZAPSAT } from '../api/karta-zamestnance';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import type * as SnimekObrazovky from './snimek-obrazovky';
import type { PorizeniSnimku } from './snimek-obrazovky';

const Nahlasit = lina(() => import('./NahlasitUraz').then((m) => m.Nahlasit));

// Modul snímku (zakrývání citlivých míst, klon stránky) se stahuje až po načtení aplikace, ne v hlavním balíku
// (rozpočet 256 KiB, nasazení vlaku C3 31. 10. 2026). Stažený modul se drží, ať klepnutí na ⚑ fotí synchronně.
let modulSnimku: typeof SnimekObrazovky | undefined;
const nacistModulSnimku = () =>
  import('./snimek-obrazovky').then((m) => {
    modulSnimku = m;
    return m;
  });

/** Nejdéle tolik se čeká na stažení modulu snímku; pak se okno otevře bez snímku (výpadek sítě). */
const STAZENI_MODULU_MS = 3000;

/** Snímek ihned, je-li modul stažený; jinak až po jeho stažení (stránka se do té doby nemění, okno ještě není). */
function vyfotit(): PorizeniSnimku {
  if (modulSnimku) return modulSnimku.vyfotitStranku();
  const porizeni = Promise.race([
    nacistModulSnimku().then((m) => m.vyfotitStranku()),
    new Promise<null>((hotovo) => window.setTimeout(() => hotovo(null), STAZENI_MODULU_MS)),
  ]).catch(() => null);
  return {
    soubor: porizeni.then((p) => (p ? p.soubor : null)),
    naklonovano: porizeni.then((p) => p?.naklonovano),
  };
}

export function NahlasitTlacitko({ ja }: { ja: Ja }) {
  const [otevreno, setOtevreno] = useState(false);
  // Snímek obrazovky k hlášení závady / námětu (viz snimek-obrazovky.ts): továrna, kterou formulář závady zavolá.
  const [snimek, setSnimek] = useState<{ porizeni: () => Promise<File | null> }>();
  const akce = ja.akce ?? [];
  const smiAplikace = akce.includes(AKCE_PODAT);
  // Okno se otevírá rovnou provozním hlášením (incident, úraz), když to účet smí — všechny role to smí (`VSICHNI`).
  const smiProvoz = akce.includes(AKCE_URAZ_ZAPSAT) || akce.includes(AKCE_UDALOST_NAHLASIT);
  // Knihovnu na snímek stáhnout v klidu po načtení, ať klepnutí nečeká na síť.
  useEffect(() => {
    if (!smiAplikace) return;
    const nacist = () =>
      void nacistModulSnimku()
        .then((m) => m.pripravitSnimek())
        .catch(() => undefined);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(nacist, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(nacist, 2000);
    return () => window.clearTimeout(id);
  }, [smiAplikace]);
  const fotim = useRef(false);
  const otevrit = () => {
    if (fotim.current) return;
    if (!smiAplikace) {
      setOtevreno(true);
      return;
    }
    if (smiProvoz) {
      // Okno se otevírá provozním hlášením (úraz, incident), kde se snímek nepoužije: nic se neklonuje (tablet
      // se nezpomalí) a okno se otevře hned. Snímek se pořídí, až kdo přepne na Závadu / Námět — stránka za oknem
      // je pořád ta s chybou a okno Nahlásit se ze snímku vyřazuje (`jeOknoNahlasit`).
      let porizeni: Promise<File | null> | undefined;
      setSnimek({ porizeni: () => (porizeni ??= vyfotit().soubor) });
      setOtevreno(true);
      return;
    }
    // Okno se otevírá rovnou závadou: snímek se pořídí při klepnutí, dřív než se okno otevře, a okno počká na klon
    // stránky (nejvýš ~0,4 s), ať na snímku není načítání okna; vykreslení do obrázku běží dál na pozadí.
    const foto = vyfotit();
    setSnimek({ porizeni: () => foto.soubor });
    fotim.current = true;
    void foto.naklonovano.then(() => {
      fotim.current = false;
      setOtevreno(true);
    });
  };
  // Krok 12.5: závadu nebo námět k aplikaci hlásí každý přihlášený (`hlaseni.podat`).
  if (
    !akce.includes(AKCE_URAZ_ZAPSAT) &&
    !akce.includes(AKCE_UDALOST_NAHLASIT) &&
    !akce.includes(AKCE_PODAT)
  ) {
    return null;
  }
  return (
    <>
      <button
        type="button"
        className="hlavicka__nahlasit"
        title="Nahlásit — incident, havárii, neshodu, podnět, pracovní úraz, závadu aplikace nebo námět"
        aria-label="Nahlásit"
        aria-haspopup="dialog"
        aria-expanded={otevreno}
        onClick={otevrit}
        data-testid="nahlasit"
      >
        <Ikona id="flag" velikost={16} />
        <span className="hlavicka__nahlasit-text">Nahlásit</span>
      </button>
      {otevreno && (
        <NacitaniCasti>
          <Nahlasit
            ja={ja}
            naZavrit={() => {
              setOtevreno(false);
              setSnimek(undefined);
            }}
            snimek={snimek?.porizeni}
          />
        </NacitaniCasti>
      )}
    </>
  );
}
